import {
  sanitizeTreeRuntimeMissingParentReferenceLifecycleEvidence,
  type TreeRuntimeMissingParentReferenceLifecycleEvidence,
} from './TreeRuntimeDiscrepancyCohortLifecycleEvidence';

export const TREE_RUNTIME_DISCREPANCY_COHORT_LIFECYCLE_PROBE_FUNCTION =
  'observe_tree_discrepancy_cohort_lifecycle';

export interface TreeRuntimeDiscrepancyCohortLifecycleProbeRequest {
  readonly authenticatedSession: true;
  readonly acceptsCandidateIds: false;
  readonly derivesAuthenticatedOwnerFromSession: true;
  readonly derivesReferencesFromActiveGraphRows: true;
}

export interface TreeRuntimeDiscrepancyCohortLifecycleProbeResponse {
  readonly ok: true;
  readonly readOnly: true;
  readonly repairExecuted: false;
  readonly identitySuppressed: true;
  readonly candidateIdsAccepted: false;

  readonly missingParentReferenceLifecycle:
    TreeRuntimeMissingParentReferenceLifecycleEvidence;
}

const RESPONSE_FIELDS =
  Object.freeze([
    'ok',
    'readOnly',
    'repairExecuted',
    'identitySuppressed',
    'candidateIdsAccepted',
    'missingParentReferenceLifecycle',
  ] as const);

const hasExactResponseFields = (
  value: Record<string, unknown>,
): boolean => {
  const actual =
    Object.keys(value).sort();

  const expected =
    [...RESPONSE_FIELDS].sort();

  return (
    actual.length === expected.length &&
    actual.every(
      (key, index) =>
        key === expected[index],
    )
  );
};

export const sanitizeTreeRuntimeDiscrepancyCohortLifecycleProbeResponse =
  (
    candidate: unknown,
  ): TreeRuntimeDiscrepancyCohortLifecycleProbeResponse | null => {
    if (
      !candidate ||
      typeof candidate !== 'object'
    ) {
      return null;
    }

    const value =
      candidate as Record<string, unknown>;

    if (!hasExactResponseFields(value)) {
      return null;
    }

    if (
      value.ok !== true ||
      value.readOnly !== true ||
      value.repairExecuted !== false ||
      value.identitySuppressed !== true ||
      value.candidateIdsAccepted !== false
    ) {
      return null;
    }

    const missingParentReferenceLifecycle =
      sanitizeTreeRuntimeMissingParentReferenceLifecycleEvidence(
        value.missingParentReferenceLifecycle,
      );

    if (!missingParentReferenceLifecycle) {
      return null;
    }

    return Object.freeze({
      ok: true,
      readOnly: true,
      repairExecuted: false,
      identitySuppressed: true,
      candidateIdsAccepted: false,
      missingParentReferenceLifecycle,
    });
  };

export const createTreeRuntimeDiscrepancyCohortLifecycleProbeRequest =
  (): TreeRuntimeDiscrepancyCohortLifecycleProbeRequest =>
    Object.freeze({
      authenticatedSession: true,
      acceptsCandidateIds: false,
      derivesAuthenticatedOwnerFromSession: true,
      derivesReferencesFromActiveGraphRows: true,
    });
